import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { ApiError } from '@/lib/api/client';
import { ConsentCapturePanel, type ConsentAnswerValue } from './ConsentCapturePanel';
import { useConsentRequirements, useRecordConsents } from './useConsent';
import type { ConsentTypeId } from './types';

/**
 * Consent is asked once per patient at Registration. Two things can make it due
 * again afterwards, and neither passes back through Registration:
 *
 *   * the hospital configured an expiry period and it has lapsed, and
 *   * a returning patient who registered before this feature existed, and so
 *     has no Treatment Consent on file at all.
 *
 * A returning patient skips Verify and Register entirely (see
 * routes/visits.js's POST /start), so without this banner the re-ask would have
 * nowhere to happen — it would sit unnoticed in Patient Records. This puts it
 * in front of whoever opens the visit, and nowhere else: when nothing is due it
 * renders nothing at all, which is the normal case for almost every visit.
 *
 * Deliberately NOT a blocker. A lapsed consent on a patient already in the
 * building is a paperwork problem, not a reason to refuse them care — the hard
 * gate lives at Registration, where it belongs.
 */
export function ConsentDueBanner({ uhid }: { uhid: string | null | undefined }) {
  const { data } = useConsentRequirements(uhid);
  const save = useRecordConsents(uhid);
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState<Partial<Record<ConsentTypeId, ConsentAnswerValue>>>({});
  const [error, setError] = useState('');

  const due = (data?.items || []).filter((item) => item.due);
  if (!uhid || due.length === 0) return null;

  const unanswered = due.filter((item) => !answers[item.consent_type]);

  const submit = async () => {
    setError('');
    try {
      await save.mutateAsync({
        consents: due.map((item) => ({
          consent_type: item.consent_type,
          granted: answers[item.consent_type] === 'accept',
        })),
      });
      setOpen(false);
      setAnswers({});
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to record consent');
    }
  };

  return (
    <div className="mx-6 mt-3 rounded-xl border border-warn bg-warn-soft p-3.5">
      <div className="flex flex-wrap items-center gap-2.5">
        <span className="text-[13px] font-semibold text-warn-ink">
          Consent due: {due.map((item) => item.label).join(', ')}
        </span>
        <span className="text-[12.5px] text-warn-ink/80">
          {due.some((item) => item.status === 'expired')
            ? 'This consent has passed its validity period and needs to be taken again.'
            : 'This patient has no answer on file for this consent yet.'}
        </span>
        <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide' : 'Record now'}
        </Button>
      </div>

      {open && (
        <div className="mt-3 flex flex-col gap-3">
          <ConsentCapturePanel
            items={due}
            answers={answers}
            onAnswer={(type, value) => setAnswers((prev) => ({ ...prev, [type]: value }))}
            disabled={save.isPending}
          />
          {error && <div className="text-[12.5px] text-danger">{error}</div>}
          <div className="flex items-center gap-2">
            <Button onClick={submit} disabled={unanswered.length > 0 || save.isPending} loading={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save consent'}
            </Button>
            {unanswered.length > 0 && (
              <span className="text-[12.5px] text-warn-ink">
                {unanswered.length} still unanswered
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
